import type { ComponentProps, PropsWithChildren } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { WidthProps } from 'styled-system';
import { color, display, width } from 'styled-system';

import { Link } from 'common/components/Link';
import { BodyRegularBR2 } from 'common/components/Typography';
import { getThemeColor } from 'common/theme/helper';
import { focusOutlineCSSWithOffsetAndRadius } from 'common/theme/styles';
import { getTrackerProps } from 'common/tracking/getTrackerProps';
import type { ImageSectionItem, LinkSectionItem } from '../types';

interface ItemProps extends PropsWithChildren {
  item: LinkSectionItem | ImageSectionItem;
}

const UnstyledItemLink = styled(Link)<any>`
  ${display};
  ${color};
  ${width};
  ${focusOutlineCSSWithOffsetAndRadius};

  &:hover {
    color: ${getThemeColor('sys.primary.text.hover')};
  }

  &:active {
    color: ${getThemeColor('sys.primary.text.active')};
  }
`;

const ItemLink = (props: ComponentProps<typeof UnstyledItemLink>) => (
  <UnstyledItemLink display="block" width={1} styled={false} {...props} />
);

const UnstyledItemText = styled.div<WidthProps>`
  ${width};
`;

const ItemText = (props: ComponentProps<typeof UnstyledItemText>) => (
  <UnstyledItemText width={1} {...props} />
);

const getItemTrackerProps = (item: LinkSectionItem | ImageSectionItem) => {
  const { name: label } = item;

  return getTrackerProps(
    {
      'data-testid': 'link-section-link',
      ...('href' in item && { href: item.href }),
      ...('title' in item && { title: item.title }),
      ...('target' in item && { target: item.target }),
      ...('target' in item &&
        item.target === '_blank' && {
          rel: 'noreferrer',
        }),
      onClick(event) {
        // Prevent tracking if the element is not an anchor or image
        event.nativeEvent.trackingPreventDefault = event.currentTarget.nodeName !== 'A';
      },
    },
    {
      event: 'footer_tracking',
      placement: 'linkSection',
      label_identifier: 'h24_footer_link_section',
      ...('image' in item && { label }),
    }
  );
};

const Item = ({ item, children }: ItemProps) => {
  const ItemComponent = 'href' in item && item.href ? ItemLink : ItemText;

  return (
    <BodyRegularBR2 color="sys.neutral.text.strong">
      <ItemComponent {...getItemTrackerProps(item)}>{children}</ItemComponent>
    </BodyRegularBR2>
  );
};

export default Item;
